import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { distinctUntilChanged, filter, Subscription, take } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormControl, AbstractControl } from '@angular/forms';
// Services
import { CvParseFacadeService } from '../../../../services/facades/cv-parse.facade.service';
import { MatDialogService } from '@my7n/ui';
// Validators
import { PDF_VALID_FILE_EXT, PDF_VALID_MIME_TYPE } from '../../../../interfaces/file-upload.interface';
import { FileExtensionValidator, FileSizeLimitValidator, FileMimeTypeValidator } from '../../../../validators/files-upload.validator';
import { CvParseBroadcastChannelService } from '../../../../services/cv/cv-parse-channel.service';
import { BROADCAST_MESSAGES } from '../../../../interfaces/cv-parse';

export interface ICvParseDialogData {
  userId?: string;
  cvId?: number;
}

@Component({
    selector: 'cv-parse-dialog',
    templateUrl: './cv-parse-dialog.component.html',
    styleUrls: ['./cv-parse-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CvParseDialogComponent implements OnInit, OnDestroy {
  CV_VALID_FILE_EXT = [PDF_VALID_FILE_EXT];
  CV_FILE_MAX_SIZE = 5;
  CV_VALID_MIME_TYPES = [PDF_VALID_MIME_TYPE];

  cvParseForm: FormGroup<{
    cvToParse: FormControl<File | null>;
  }>;

  protected subscriptions$ = new Subscription();

  get cvToParseControl(): AbstractControl<File, File> {
    return this.cvParseForm.get('cvToParse');
  }

  constructor(
    public dialogRef: MatDialogRef<CvParseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICvParseDialogData,
    public dialog: MatDialogService,
    private cvParseFacadeService: CvParseFacadeService,
    private cvParseBroadcastChannelService: CvParseBroadcastChannelService
  ) { }

  ngOnInit() {
    this.setupCvParseForm();
  }

  private setupCvParseForm() {
    this.cvParseForm = new FormGroup({
      'cvToParse': new FormControl<File | null>(null, [
        FileSizeLimitValidator(this.CV_FILE_MAX_SIZE),
        FileExtensionValidator(this.CV_VALID_FILE_EXT),
        FileMimeTypeValidator(this.CV_VALID_MIME_TYPES)
      ])
    });

    this.registerCvUploadHandler();
  }

  private registerCvUploadHandler() {
    this.subscriptions$.add(this.cvToParseControl.valueChanges.pipe(
      distinctUntilChanged(),
      filter(() => this.cvToParseControl.valid), // only proceed when we have valid files uploaded
    ).subscribe((uploadedFile) => {
      this.handleParseCvFile(uploadedFile);
    }));
  }

  private handleParseCvFile(file: File) {
    this.cvParseFacadeService.uploadFileToParse(this.data?.userId, file);

    this.cvParseFacadeService.uploadFileStatus$
      .pipe(
        filter((status) => !status.loading && !status.error),
        take(1)
      )
      .subscribe(() => {
        this.closeDialog();
        this.cvParseBroadcastChannelService.sendMessage(BROADCAST_MESSAGES.CHECK_ACTIVE_PARSING);
      });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
  }
}

